import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import styled, { createGlobalStyle } from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { useAppSelector, useAppDispatch } from '../../store/hooks';
import { useTranslation } from 'react-i18next';
import { UserRole } from '../../types/portal';
import logoImage from '../../assets/logo-l.png';
import { logoutUser } from '../../store/slices/authSlice';
import Avatar from '../common/Avatar';
import LanguageSwitcher from '../LanguageSwitcher';
import { API_BASE_URL } from '../../services/api';

// Inline modern SVG icons using currentColor for stroke
const IconBase: React.FC<React.SVGProps<SVGSVGElement>> = ({ width = 22, height = 22, children, ...props }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width={width} height={height} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" {...props}>
    {children}
  </svg>
);

const HomeIcon = () => (
  <IconBase>
    <path d="M3 10.5 12 3l9 7.5"/>
    <path d="M5 10.5V21h14V10.5"/>
  </IconBase>
);
const ClipboardListIcon = () => (
  <IconBase>
    <rect x="4" y="4" width="16" height="18" rx="2"/>
    <path d="M9 4h6v3H9z"/>
    <line x1="8" y1="12" x2="16" y2="12"/>
    <line x1="8" y1="16" x2="16" y2="16"/>
  </IconBase>
);
const UsersIcon = () => (
  <IconBase>
    <path d="M16 11c1.657 0 3-1.79 3-4s-1.343-4-3-4-3 1.79-3 4 1.343 4 3 4z"/>
    <path d="M8 13c2.21 0 4-2.239 4-5s-1.79-5-4-5-4 2.239-4 5 1.79 5 4 5z"/>
    <path d="M2 21v-1a6 6 0 0 1 6-6h0"/>
    <path d="M14 14a6 6 0 0 1 6 6v1"/>
  </IconBase>
);
const TeacherIcon = () => (
  <IconBase>
    <rect x="2" y="7" width="20" height="13" rx="2"/>
    <path d="M12 7V3"/>
    <path d="M8 3h8"/>
  </IconBase>
);
const CreditCardIcon = () => (
  <IconBase>
    <rect x="2" y="4" width="20" height="16" rx="2"/>
    <line x1="2" y1="10" x2="22" y2="10"/>
    <line x1="6" y1="16" x2="10" y2="16"/>
  </IconBase>
);
const SettingsIcon = () => (
  <IconBase>
    <circle cx="12" cy="12" r="3"/>
    <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V22a2 2 0 1 1-4 0v-.11a1.65 1.65 0 0 0-1-1.51 1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H2a2 2 0 1 1 0-4h.11a1.65 1.65 0 0 0 1.51-1 1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H8a1.65 1.65 0 0 0 1-1.51V2a2 2 0 1 1 4 0v.11a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V8c0 .66.26 1.3.73 1.77.47.47 1.11.73 1.77.73H22a2 2 0 1 1 0 4h-.11a1.65 1.65 0 0 0-1.51 1z"/>
  </IconBase>
);
const MenuIcon = () => (
  <IconBase>
    <line x1="3" y1="12" x2="21" y2="12" />
    <line x1="3" y1="6" x2="21" y2="6" />
    <line x1="3" y1="18" x2="21" y2="18" />
  </IconBase>
);

const XIcon = () => (
  <IconBase>
    <line x1="18" y1="6" x2="6" y2="18" />
    <line x1="6" y1="6" x2="18" y2="18" />
  </IconBase>
);

const LogoutIcon = () => (
  <IconBase>
    <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/>
    <polyline points="16 17 21 12 16 7"/>
    <line x1="21" y1="12" x2="9" y2="12"/>
  </IconBase>
);

// Styled components matching the home page Header — converted to a left sidebar for portal
const HeaderWrapper = styled.aside`
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    height: var(--portal-topbar-height, 64px);
    width: 100%;
    background: #ffffff;
    z-index: 1000;
    border-bottom: 1px solid var(--border-light);
    box-shadow: 0 2px 10px rgba(0,0,0,0.05);
    display: flex;
    flex-direction: row;
    align-items: center;
    padding: 0;

    /* Mobile adjustments */
    @media (max-width: 768px) {
        height: 60px;
        box-shadow: 0 4px 12px rgba(0,0,0,0.08);
    }
`;

const Nav = styled.nav`
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    width: 100%;
    max-width: 1200px;
    margin: 0 auto;
    height: 100%;
    padding: 0 20px;

    @media (min-width: 1241px) {
        padding: 0;
    }

    @media (max-width: 768px) {
        gap: 0.5rem;
        padding: 0 16px;
    }
`;

const GlobalNavStyle = createGlobalStyle`
    :root {
        --header-text: #111827;
        --border-light: rgba(0, 0, 0, 0.08);
    }
`;

const LogoLink = styled(Link)`
    text-decoration: none;
    cursor: pointer;
    display: flex;
    align-items: center;
    gap: 0.75rem;

    @media (max-width: 768px) {
        display: none;
    }
`;

const LogoImage = styled.img`
    height: 28px;
    width: auto;
    transition: transform 0.3s ease;

    &:hover {
        transform: scale(1.05);
    }
`;

const CenterLinks = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 0.5rem;
    margin-left: 2rem;

    a {
        display: inline-flex;
        align-items: center;
        gap: 0.5rem;
        color: var(--header-text);
        text-decoration: none;
        font-weight: 600;
        font-size: var(--font-size-p);
        transition: background 0.2s ease, color 0.2s ease;
        padding: 0.6rem 0.75rem;
        border-radius: 8px;
        white-space: nowrap;
    }

    a:hover {
        color: var(--header-text);
        background: rgba(0,0,0,0.04);
    }

    /* Mobile: hide on top bar */
    @media (max-width: 768px) {
        display: none;
    }
`;

const RightActions = styled.div`
    margin-left: auto;
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 1rem;

    /* Hide on mobile (profile + logout move into menu) */
    @media (max-width: 768px) {
        display: none;
    }
`;

const FooterButton = styled.button`
    background: transparent;
    border: none;
    border-radius: 8px;
    color: var(--header-text);
    padding: 0.6rem 0.75rem;
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
    cursor: pointer;
    text-align: left;
    transition: background 0.2s ease, border-color 0.2s ease;
    font-weight: 600;
    &:hover { background: rgba(0,0,0,0.04); }
    &:focus-visible { outline: none; box-shadow: 0 0 0 3px rgba(0,0,0,0.15); }
`;

const AvatarCircle = styled.div`
    width: 36px;
    height: 36px;
    border-radius: 0;
    background: transparent;
    color: var(--header-text);
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: 700;
    text-transform: uppercase;
    overflow: hidden;
    border: none;

    @media (max-width: 768px) {
        width: 28px;
        height: 28px;
        border-radius: 50%;
    }
`;

const AvatarImg = styled.img`
    width: 100%;
    height: 100%;
    object-fit: cover;
    border-radius: 0;
`;

const MobileOnly = styled.div`
    display: none;
    @media (max-width: 768px) {
        display: flex;
        align-items: center;
        justify-content: space-between;
        width: 100%;
    }
`;

const BurgerButton = styled.button`
    background: transparent;
    border: none;
    color: var(--header-text);
    padding: 0.5rem;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    z-index: 1100;
    transition: transform 0.2s ease;

    &:active {
        transform: scale(0.9);
    }
`;

const DesktopOnly = styled.div`
    display: flex;
    align-items: center;
    @media (max-width: 768px) {
        display: none;
    }
`;

const DesktopOnlyLanguageWrapper = styled(DesktopOnly)`
    margin-left: 1rem;
`;

const MobileMenuOverlay = styled(motion.div)`
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.3);
    backdrop-filter: blur(4px);
    z-index: 1050;
`;

const MobileMenuContainer = styled(motion.div)`
    position: fixed;
    top: 0;
    right: 0;
    bottom: 0;
    width: 280px;
    height: 100dvh;
    background: #ffffff;
    z-index: 1060;
    padding: calc(5rem + env(safe-area-inset-top)) 1.5rem calc(2rem + env(safe-area-inset-bottom));
    display: flex;
    flex-direction: column;
    gap: 1rem;
    box-shadow: -10px 0 30px rgba(0, 0, 0, 0.05);
    overflow-y: auto;

    a, button {
        display: flex;
        align-items: center;
        gap: 1rem;
        padding: 0.75rem 1rem;
        border-radius: 12px;
        color: var(--header-text);
        text-decoration: none;
        font-weight: 600;
        font-size: 1rem;
        background: transparent;
        border: none;
        width: 100%;
        text-align: left;
        transition: background 0.2s ease;

        &:hover {
            background: rgba(0, 0, 0, 0.04);
        }

        &.active {
            background: rgba(var(--primary-orange-rgb, 236, 92, 44), 0.1);
            color: var(--primary-orange);
        }
    }

    .menu-section-label {
        font-size: 0.75rem;
        font-weight: 700;
        text-transform: uppercase;
        color: #6b7280;
        letter-spacing: 0.05em;
        margin: 1.5rem 1rem 0.5rem;
    }

    .profile-item {
        margin-top: auto;
        padding-top: 1.5rem;
        border-top: 1px solid var(--border-light);
    }
`;



const Navigation: React.FC = () => {
    const { t, i18n } = useTranslation();
    const navigate = useNavigate();
    const location = useLocation();
    const dispatch = useAppDispatch();
    const { user, isAuthenticated } = useAppSelector((state) => state.auth);
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const [avatarBust, setAvatarBust] = useState<number>(0);

    useEffect(() => {
        const handler = () => setAvatarBust(Date.now());
        window.addEventListener('avatar-updated', handler as EventListener);
        return () => window.removeEventListener('avatar-updated', handler as EventListener);
    }, []);

    const getInitials = () => {
        const fn = (user?.firstName || '').trim();
        const ln = (user?.lastName || '').trim();
        if (fn || ln) {
            return `${fn.charAt(0)}${ln.charAt(0)}` || (user?.username || user?.email || 'U').charAt(0);
        }
        const fromUsername = (user?.username || '').charAt(0);
        const fromEmail = (user?.email || '').charAt(0);
        return (fromUsername || fromEmail || 'U').toUpperCase();
    };

    const goToProfile = () => {
        if (!user) return;
        navigate('/portal/profile', { state: { scrollTo: 'profile' } });
    };

    const profilePic = user?.profilePicture || user?.profileImage || undefined;

    useEffect(() => {
        if (!isAuthenticated) {
            navigate('/');
        }
    }, [isAuthenticated, navigate]);

    if (!isAuthenticated || !user) {
        return (
            <HeaderWrapper>
                <GlobalNavStyle />
                <Nav>
                    <LogoLink to="/">
                        <LogoImage src={logoImage} alt="DaaS logo" />
                    </LogoLink>
                    <DesktopOnlyLanguageWrapper>
                        <LanguageSwitcher />
                    </DesktopOnlyLanguageWrapper>
                </Nav>
            </HeaderWrapper>
        );
    }

    const getDashboardPath = () => {
        switch (user.role) {
            case UserRole.STUDENT:
                return '/portal/student';
            case UserRole.TEACHER:
                return '/portal/teacher';
            default:
                return '/';
        }
    };

    type NavItem = { name: string; href: string; icon?: React.ReactNode; isButton?: boolean };
    const getNavigationItems = (): NavItem[] => {
        if (user.role === UserRole.STUDENT) {
            return [
                { name: 'Book new lesson', href: '/portal/lesson#available', isButton: true },
            ];
        }
        if (user.role === UserRole.TEACHER) {
            return [
                { name: 'Lessons', href: '/portal/lesson' },
                { name: 'Students', href: '/portal/teacher/students', icon: <UsersIcon /> },
                { name: 'Payments', href: '/portal/teacher/payments', icon: <CreditCardIcon /> },
                { name: 'Exams', href: '/portal/teacher/exams', icon: <ClipboardListIcon /> },
            ];
        }
        return [] as NavItem[];
    };


    const navigationItems = getNavigationItems();
    const currentPath = location.pathname;
    const dashboardPath = getDashboardPath();

    const handleLogout = async () => {
        setIsMenuOpen(false);
        try {
            await dispatch(logoutUser());
        } catch (_) {}
    };

    return (
        <HeaderWrapper>
            <Nav>
                <LogoLink to={dashboardPath}>
                    <LogoImage src={logoImage} alt="DaaS logo" />
                </LogoLink>
                <GlobalNavStyle />
                
                {/* Desktop Nav */}
                <GlobalNavStyle />
                
                {/* Mobile Burger Menu */}
                <MobileOnly>
                    <LogoLink to={dashboardPath} style={{ display: 'flex' }}>
                        <LogoImage src={logoImage} alt="DaaS logo" />
                    </LogoLink>
                    <BurgerButton 
                        onClick={() => setIsMenuOpen(!isMenuOpen)}
                        aria-label={isMenuOpen ? "Close menu" : "Open menu"}
                    >
                        {isMenuOpen ? <XIcon /> : <MenuIcon />}
                    </BurgerButton>

                    <AnimatePresence>
                        {isMenuOpen && (
                            <>
                                <MobileMenuOverlay 
                                    initial={{ opacity: 0 }}
                                    animate={{ opacity: 1 }}
                                    exit={{ opacity: 0 }}
                                    onClick={() => setIsMenuOpen(false)}
                                />
                                <MobileMenuContainer
                                    initial={{ x: '100%' }}
                                    animate={{ x: 0 }}
                                    exit={{ x: '100%' }}
                                    transition={{ type: 'spring', damping: 25, stiffness: 200 }}
                                >
                                    <div className="menu-section-label">{t('nav.navigation', 'Navigation')}</div>
                                    <Link 
                                        to={dashboardPath} 
                                        className={currentPath === dashboardPath ? 'active' : ''}
                                        onClick={() => setIsMenuOpen(false)}
                                    >
                                        <HomeIcon /> {t('nav.dashboard', 'Dashboard')}
                                    </Link>
                                    
                                    {user.role === UserRole.STUDENT && (
                                        <>
                                            <Link 
                                                to="/portal/student/agenda" 
                                                className={currentPath === '/portal/student/agenda' ? 'active' : ''}
                                                onClick={() => setIsMenuOpen(false)}
                                            >
                                                <CreditCardIcon /> {t('nav.agenda', 'My Agenda')}
                                            </Link>
                                            <Link 
                                                to="/portal/lesson#available" 
                                                className={currentPath === '/portal/lesson' && location.hash === '#available' ? 'active' : ''}
                                                onClick={() => setIsMenuOpen(false)}
                                            >
                                                <HomeIcon /> {t('nav.book_lesson', 'Book Lesson')}
                                            </Link>
                                        </>
                                    )}

                                    {user.role === UserRole.TEACHER && (
                                        <>
                                            <Link 
                                                to="/portal/lesson" 
                                                className={currentPath === '/portal/lesson' ? 'active' : ''}
                                                onClick={() => setIsMenuOpen(false)}
                                            >
                                                <HomeIcon /> {t('nav.lessons', 'Lessons')}
                                            </Link>
                                            <Link 
                                                to="/portal/teacher/students" 
                                                className={currentPath === '/portal/teacher/students' ? 'active' : ''}
                                                onClick={() => setIsMenuOpen(false)}
                                            >
                                                <UsersIcon /> {t('nav.students', 'Students')}
                                            </Link>
                                            <Link 
                                                to="/portal/teacher/payments" 
                                                className={currentPath === '/portal/teacher/payments' ? 'active' : ''}
                                                onClick={() => setIsMenuOpen(false)}
                                            >
                                                <CreditCardIcon /> {t('nav.payments', 'Payments')}
                                            </Link>
                                            <Link 
                                                to="/portal/teacher/exams" 
                                                className={currentPath.startsWith('/portal/teacher/exams') ? 'active' : ''}
                                                onClick={() => setIsMenuOpen(false)}
                                            >
                                                <ClipboardListIcon /> {t('nav.exams', 'Exams')}
                                            </Link>
                                        </>
                                    )}

                                    {user.role === UserRole.ADMIN && (
                                        <>
                                            <Link 
                                                to="/portal/admin/students" 
                                                className={currentPath === '/portal/admin/students' ? 'active' : ''}
                                                onClick={() => setIsMenuOpen(false)}
                                            >
                                                <UsersIcon /> {t('nav.students', 'Students')}
                                            </Link>
                                            <Link 
                                                to="/portal/admin/teachers" 
                                                className={currentPath === '/portal/admin/teachers' ? 'active' : ''}
                                                onClick={() => setIsMenuOpen(false)}
                                            >
                                                <TeacherIcon /> {t('nav.teachers', 'Teachers')}
                                            </Link>
                                            <Link 
                                                to="/portal/admin/payments" 
                                                className={currentPath === '/portal/admin/payments' ? 'active' : ''}
                                                onClick={() => setIsMenuOpen(false)}
                                            >
                                                <CreditCardIcon /> {t('nav.payments', 'Payments')}
                                            </Link>
                                            <Link 
                                                to="/portal/admin/exams" 
                                                className={currentPath.startsWith('/portal/admin/exams') ? 'active' : ''}
                                                onClick={() => setIsMenuOpen(false)}
                                            >
                                                <ClipboardListIcon /> {t('nav.exams', 'Exams')}
                                            </Link>
                                            <Link 
                                                to="/portal/lesson" 
                                                className={currentPath === '/portal/lesson' ? 'active' : ''}
                                                onClick={() => setIsMenuOpen(false)}
                                            >
                                                <HomeIcon /> {t('nav.lessons', 'Lessons')}
                                            </Link>
                                            <Link 
                                                to="/portal/admin/parameters" 
                                                className={currentPath === '/portal/admin/parameters' ? 'active' : ''}
                                                onClick={() => setIsMenuOpen(false)}
                                            >
                                                <SettingsIcon /> {t('nav.parameters', 'Parameters')}
                                            </Link>
                                        </>
                                    )}

                                    <div className="menu-section-label">{t('nav.settings', 'Settings')}</div>
                                    <div style={{ padding: '0 1rem' }}>
                                        <LanguageSwitcher onLanguageChange={() => setIsMenuOpen(false)} />
                                    </div>

                                    <div className="profile-item">
                                        <Link to="/portal/profile" onClick={() => setIsMenuOpen(false)}>
                                            <Avatar size={32} rounded src={`${API_BASE_URL}/api/users/me/avatar?v=${avatarBust}`} fallbackSrc={profilePic ? String(profilePic) : null} initials={getInitials()} />
                                            <span>{t('nav.my_profile', 'My Profile')}</span>
                                        </Link>
                                        <button onClick={handleLogout}>
                                            <LogoutIcon /> {t('nav.logout', 'Logout')}
                                        </button>
                                    </div>
                                </MobileMenuContainer>
                            </>
                        )}
                    </AnimatePresence>
                </MobileOnly>


                <RightActions>
                    {user.role === UserRole.STUDENT && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginRight: '1rem' }} className="desktop-only">
                            <Link
                                to="/portal/student"
                                onClick={() => setIsMenuOpen(false)}
                                aria-label={String(t('nav.dashboard', 'Dashboard'))}
                                title={String(t('nav.dashboard', 'Dashboard'))}
                                style={{ 
                                    color: currentPath === '/portal/student' ? 'var(--primary-orange)' : 'var(--header-text)',
                                    textDecoration: 'none',
                                    fontWeight: 600,
                                    padding: '0.6rem 0.75rem',
                                    borderRadius: '8px'
                                }}
                            >
                                <span>{t('nav.dashboard', 'Dashboard')}</span>
                            </Link>
                            <Link
                                to="/portal/student/agenda"
                                onClick={() => setIsMenuOpen(false)}
                                aria-label={String(t('nav.agenda', 'My Agenda'))}
                                title={String(t('nav.agenda', 'My Agenda'))}
                                style={{ 
                                    color: currentPath === '/portal/student/agenda' ? 'var(--primary-orange)' : 'var(--header-text)',
                                    textDecoration: 'none',
                                    fontWeight: 600,
                                    padding: '0.6rem 0.75rem',
                                    borderRadius: '8px'
                                }}
                            >
                                <span>{t('nav.agenda', 'My Agenda')}</span>
                            </Link>
                        </div>
                    )}

                    {user.role === UserRole.TEACHER && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginRight: '1rem' }} className="desktop-only">
                            <Link to="/portal/teacher" onClick={() => setIsMenuOpen(false)} style={{ color: currentPath === '/portal/teacher' ? 'var(--primary-orange)' : 'var(--header-text)', textDecoration: 'none', fontWeight: 600, padding: '0.6rem 0.75rem', borderRadius: '8px' }}>
                                <span>{t('nav.dashboard', 'Dashboard')}</span>
                            </Link>
                            <Link to="/portal/lesson" onClick={() => setIsMenuOpen(false)} style={{ color: currentPath === '/portal/lesson' ? 'var(--primary-orange)' : 'var(--header-text)', textDecoration: 'none', fontWeight: 600, padding: '0.6rem 0.75rem', borderRadius: '8px' }}>
                                <span>{t('nav.lessons', 'Lessons')}</span>
                            </Link>
                            <Link to="/portal/teacher/students" onClick={() => setIsMenuOpen(false)} style={{ color: currentPath === '/portal/teacher/students' ? 'var(--primary-orange)' : 'var(--header-text)', textDecoration: 'none', fontWeight: 600, padding: '0.6rem 0.75rem', borderRadius: '8px' }}>
                                <span>{t('nav.students', 'Students')}</span>
                            </Link>
                            <Link to="/portal/teacher/payments" onClick={() => setIsMenuOpen(false)} style={{ color: currentPath === '/portal/teacher/payments' ? 'var(--primary-orange)' : 'var(--header-text)', textDecoration: 'none', fontWeight: 600, padding: '0.6rem 0.75rem', borderRadius: '8px' }}>
                                <span>{t('nav.payments', 'Payments')}</span>
                            </Link>
                            <Link to="/portal/teacher/exams" onClick={() => setIsMenuOpen(false)} style={{ color: currentPath.startsWith('/portal/teacher/exams') ? 'var(--primary-orange)' : 'var(--header-text)', textDecoration: 'none', fontWeight: 600, padding: '0.6rem 0.75rem', borderRadius: '8px' }}>
                                <span>{t('nav.exams', 'Exams')}</span>
                            </Link>
                        </div>
                    )}

                    {user.role === UserRole.ADMIN && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginRight: '1rem' }} className="desktop-only">
                            <Link to="/portal/admin" onClick={() => setIsMenuOpen(false)} style={{ color: currentPath === '/portal/admin' ? 'var(--primary-orange)' : 'var(--header-text)', textDecoration: 'none', fontWeight: 600, padding: '0.6rem 0.75rem', borderRadius: '8px' }}>
                                <span>{t('nav.dashboard', 'Dashboard')}</span>
                            </Link>
                            <Link to="/portal/admin/students" onClick={() => setIsMenuOpen(false)} style={{ color: currentPath === '/portal/admin/students' ? 'var(--primary-orange)' : 'var(--header-text)', textDecoration: 'none', fontWeight: 600, padding: '0.6rem 0.75rem', borderRadius: '8px' }}>
                                <span>{t('nav.students', 'Students')}</span>
                            </Link>
                            <Link to="/portal/admin/teachers" onClick={() => setIsMenuOpen(false)} style={{ color: currentPath === '/portal/admin/teachers' ? 'var(--primary-orange)' : 'var(--header-text)', textDecoration: 'none', fontWeight: 600, padding: '0.6rem 0.75rem', borderRadius: '8px' }}>
                                <span>{t('nav.teachers', 'Teachers')}</span>
                            </Link>
                            <Link to="/portal/admin/payments" onClick={() => setIsMenuOpen(false)} style={{ color: currentPath === '/portal/admin/payments' ? 'var(--primary-orange)' : 'var(--header-text)', textDecoration: 'none', fontWeight: 600, padding: '0.6rem 0.75rem', borderRadius: '8px' }}>
                                <span>{t('nav.payments', 'Payments')}</span>
                            </Link>
                            <Link to="/portal/admin/exams" onClick={() => setIsMenuOpen(false)} style={{ color: currentPath.startsWith('/portal/admin/exams') ? 'var(--primary-orange)' : 'var(--header-text)', textDecoration: 'none', fontWeight: 600, padding: '0.6rem 0.75rem', borderRadius: '8px' }}>
                                <span>{t('nav.exams', 'Exams')}</span>
                            </Link>
                            <Link to="/portal/lesson" onClick={() => setIsMenuOpen(false)} style={{ color: currentPath === '/portal/lesson' ? 'var(--primary-orange)' : 'var(--header-text)', textDecoration: 'none', fontWeight: 600, padding: '0.6rem 0.75rem', borderRadius: '8px' }}>
                                <span>{t('nav.lessons', 'Lessons')}</span>
                            </Link>
                            <Link to="/portal/admin/parameters" onClick={() => setIsMenuOpen(false)} style={{ color: currentPath === '/portal/admin/parameters' ? 'var(--primary-orange)' : 'var(--header-text)', textDecoration: 'none', fontWeight: 600, padding: '0.6rem 0.75rem', borderRadius: '8px' }}>
                                <span>{t('nav.parameters', 'Parameters')}</span>
                            </Link>
                        </div>
                    )}

                    <DesktopOnlyLanguageWrapper>
                        <LanguageSwitcher />
                    </DesktopOnlyLanguageWrapper>
                    <FooterButton aria-label={String(t('nav.profile', 'Profile'))} title={String(t('nav.profile', 'Profile'))} onClick={goToProfile}>
                        <Avatar size={36} rounded src={`${API_BASE_URL}/api/users/me/avatar?v=${avatarBust}`} fallbackSrc={profilePic ? String(profilePic) : null} initials={getInitials()} />
                    </FooterButton>
                    <FooterButton aria-label={String(t('nav.logout', 'Logout'))} title={String(t('nav.logout', 'Logout'))} onClick={async () => { try { await dispatch(logoutUser()); } catch (_) {} }}>
                        <span style={{ display: 'inline-flex', alignItems: 'center' }}>{t('nav.logout', 'Logout')}</span>
                    </FooterButton>
                </RightActions>

            </Nav>
        </HeaderWrapper>
    );
};

export default Navigation;
